import { MapPin, ShieldCheck, Users } from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";

export const navbarCopyPl: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Specjalizacja",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń MVP",
};

export const aboutUsCopyPl = (
  overrides?: Partial<AboutUsCopy["header"]>,
): AboutUsCopy => ({
  header: {
    h2: "O nas",
    h3: "Polski software house do budowy MVP",
    p1: "Jesteśmy software house'em z Polski — budujemy MVP aplikacji webowych i mobilnych, produktów SaaS oraz urządzeń IoT. Pomagamy szybko zwalidować pomysł bez przepalania budżetu na pełny produkt.",
    p2: "Mateusz projektuje UX pod realnych użytkowników, Kuba buduje backend i infrastrukturę, a Michał i Tomasz odpowiadają za elektronikę i firmware, gdy MVP obejmuje urządzenie connected.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół Blues Brackets - budowa MVP dla startupów",
    ...overrides,
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z zespołem produktowym",
      description:
        "Bez warstwy account managerów — osoba na rozmowie to osoba, która projektuje zakres MVP, UX albo architekturę systemu.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy start produktu i ograniczenia budżetu",
      description:
        "Wąski zakres, jasne kryteria sukcesu i iteracje po feedbacku z rynku — zamiast wielomiesięcznego projektu „na wszystko naraz”.",
    },
    {
      Icon: ShieldCheck,
      title: "Dane i infrastruktura w UE",
      description:
        "Projektujemy z myślą o RODO i hostingu w UE — ważne przy MVP SaaS, aplikacji mobilnych i produktów IoT.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
});

export const contactFormPl: FooterCopy["contactForm"] = {
  heading: "Napisz do nas",
  fields: {
    name: {
      label: "Imię i nazwisko",
      placeholder: "Jan Kowalski",
      requiredError: "Podaj imię i nazwisko",
    },
    email: {
      label: "Adres e-mail",
      placeholder: "jan@firma.pl",
      requiredError: "Podaj adres e-mail",
      invalidError: "Podaj poprawny adres e-mail",
    },
    topic: {
      label: "Opis pomysłu / MVP",
      placeholder:
        "Krótko opisz pomysł, etap (warsztaty / wycena / budowa) i czy to appka, SaaS czy urządzenie...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
  submit: "Wyślij zapytanie",
  submitting: "Wysyłanie...",
  successMessage: "Wiadomość wysłana!",
  errorMessage: "Nie udało się wysłać wiadomości :(",
  responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
  emailSubjectPrefix: "Imię i nazwisko",
};

export const mvpClusterLinks = [
  { href: "/mvp", text: "Budowa MVP" },
  {
    href: "/mvp-aplikacji-lub-urzadzenia",
    text: "MVP aplikacji lub urządzenia",
  },
  { href: "/warsztaty-produktowe", text: "Warsztaty produktowe" },
  { href: "/cennik-mvp", text: "Cennik MVP" },
  { href: "/iot", text: "Systemy IoT" },
  { href: "/bluetooth-low-energy", text: "Bluetooth Low Energy" },
  { href: "/blog", text: "Blog" },
  { href: "/projects", text: "Projekty" },
] as const;

export const mvpKnowledgeBaseLinks = [
  {
    href: "/pl/blogs/co-to-jest-mvp",
    text: "Co to jest MVP i czym różni się od prototypu?",
  },
  {
    href: "/pl/blogs/ile-kosztuje-mvp-aplikacji",
    text: "Ile kosztuje MVP aplikacji?",
  },
  {
    href: "/pl/blogs/jak-przygotowac-sie-do-wyceny-aplikacji",
    text: "Jak przygotować się do wyceny aplikacji?",
  },
] as const;

export const organizationStructuredData = (description: string) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Blues Brackets",
  url: "https://bluesbrackets.com",
  logo: "https://bluesbrackets.com/favicon-dark.png",
  description,
  areaServed: "EU",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "hello@bluesbrackets.com",
    availableLanguage: ["Polish", "English"],
  },
});

export const serviceStructuredData = (
  name: string,
  description: string,
  serviceType: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  provider: {
    "@type": "Organization",
    name: "Blues Brackets",
  },
  description,
  areaServed: "EU",
  serviceType,
});

export const faqStructuredData = (
  faqs: readonly { q: string; a: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});
